/**
 * useChat — 챗봇 메시지 처리 hook
 *
 * 사용자 메시지 수신 → 매칭 → 응답 생성 흐름을 담당.
 *
 * Phase 1 정책 (CLAUDE.md):
 * - Rule 기반 매칭만 사용
 * - 매칭 실패 시 정해진 Fallback 메시지
 * - 인증 상태에 따라 답변 분기
 */

import { useCallback } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { matchIntent, findByIntent } from '@/lib/matcher/intentMatcher';
import { sendLog, fetchBalance } from '@/lib/api/endpoints';
import type { QAEntry } from '@/types/qa';

interface UseChatOptions {
  qaDatabase: QAEntry[];
  /** 인공 응답 지연 (ms). 기본 800ms */
  typingDelay?: number;
}

export function useChat({ qaDatabase, typingDelay = 800 }: UseChatOptions) {
  const addMessage = useChatStore((s) => s.addMessage);
  const setTyping = useChatStore((s) => s.setTyping);
  const sessionId = useChatStore((s) => s.sessionId);
  const authStatus = useAuthStore((s) => s.status);
  const context = useAuthStore((s) => s.context);

  const handleSend = useCallback(
    (userText: string) => {
      // 1. 사용자 메시지 추가
      addMessage({
        role: 'user',
        type: 'text',
        content: userText,
      });

      // 2. 타이핑 인디케이터 표시
      setTyping(true);

      // 3. 약간의 지연 후 응답 (자연스러움)
      setTimeout(() => {
        setTyping(false);

        const result = matchIntent(userText, qaDatabase, authStatus);

        if (!result.qa) {
          // Fallback 처리
          handleFallback(userText);
          return;
        }

        const qa = result.qa;

        // 비로그인 + 로그인 필수 → 로그인 유도
        if (qa.auth_required === 'required' && authStatus !== 'authenticated') {
          addMessage({
            role: 'bot',
            type: 'login_prompt',
            content:
              qa.unauth_answer ??
              `${qa.question_main}은(는) 로그인 후 이용 가능합니다.`,
            loginUrl: '/login',
          });
          return;
        }

        // 표준 답변 출력
        const answerText =
          qa.auth_required === 'mixed' && authStatus !== 'authenticated' && qa.unauth_answer
            ? qa.unauth_answer
            : qa.answer;

        addMessage({
          role: 'bot',
          type: 'text',
          content: answerText,
          ...(qa.related_links.length > 0 ? { links: qa.related_links } : {}),
          ...(qa.cta_buttons && qa.cta_buttons.length > 0
            ? { ctaButtons: qa.cta_buttons }
            : {}),
        });

        // TODO: requires_api_call이 true인 경우 → API 호출 후 결과 메시지 추가
        // TODO: escalate_to_agent가 true인 경우 → Escalation 흐름 시작
        // TODO: legal_review가 true인 경우 → AlertBanner 추가
      }, typingDelay);
    },
    [addMessage, setTyping, qaDatabase, authStatus, typingDelay]
  );

  const handleFallback = useCallback(
    (userText: string) => {
      addMessage({
        role: 'bot',
        type: 'text',
        content:
          '죄송합니다. 문의하신 내용을 정확히 이해하지 못했어요.\n\n다른 표현으로 질문해 주시거나, 자주 찾는 질문에서 선택해 주세요.',
      });

      // Fallback 로그 전송 (분석용)
      // 운영 환경에서만 실행
      if (import.meta.env.PROD) {
        // window.location을 우선 읽어 fallback 발생 시점의 실제 페이지를 기록한다.
        // init 응답이 누락·실패해도 정확한 페이지가 잡히고, 챗봇 오픈 후
        // 사용자가 페이지를 이동한 경우에도 현재 페이지가 반영된다.
        const page =
          (typeof window !== 'undefined' && window.location?.pathname) ||
          context?.current_page ||
          '/';
        sendLog({
          event: 'fallback',
          user_input: userText,
          auth_status: authStatus,
          session_id: sessionId,
          page,
        }).catch(() => {
          // 로그 전송 실패는 사용자 경험에 영향 없음
        });
      }
    },
    [addMessage, authStatus, context, sessionId]
  );

  /**
   * 잔액 조회 — intent 'balance_check' 전용 분기.
   *
   * 일반 Q&A 답변 텍스트가 아닌 BalanceCard를 띄워야 하므로 별도 처리.
   * 비로그인 / 정지 계정 / 시스템 오류는 dispatcher 응답 코드로 분기 (types/api.ts ErrorCode).
   */
  const handleBalanceCheck = useCallback(async () => {
    if (authStatus !== 'authenticated') {
      addMessage({
        role: 'bot',
        type: 'login_prompt',
        content: '잔액 조회는 로그인 후 이용 가능합니다.',
        loginUrl: '/login',
      });
      return;
    }

    setTyping(true);
    try {
      const res = await fetchBalance();
      setTyping(false);

      if (res.success && res.code === 'OK') {
        addMessage({
          role: 'bot',
          type: 'text',
          content: '현재 잔액 정보입니다.',
        });
        addMessage({
          role: 'bot',
          type: 'balance_card',
          data: res.data,
        });
        return;
      }

      // 에러 응답 분기
      if (res.code === 'AUTH_REQUIRED') {
        addMessage({
          role: 'bot',
          type: 'login_prompt',
          content: res.message || '잔액 조회는 로그인 후 이용 가능합니다.',
          loginUrl: res.meta.login_url ?? '/login',
        });
        return;
      }
      if (res.code === 'FORBIDDEN') {
        const phone = res.meta.support_phone ?? '1544-9231';
        addMessage({
          role: 'bot',
          type: 'text',
          content: `${res.message}\n\n고객센터 ${phone}으로 문의해 주세요.`,
        });
        return;
      }
      addMessage({
        role: 'bot',
        type: 'text',
        content:
          '잔액 조회 중 일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
      });
    } catch {
      setTyping(false);
      addMessage({
        role: 'bot',
        type: 'text',
        content:
          '잔액 조회 중 네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
      });
    }
  }, [addMessage, authStatus, setTyping]);

  /**
   * FAQ 메뉴 클릭 흐름: intent를 직접 사용해 Q&A를 조회한다.
   * 라벨 텍스트로 키워드 재매칭하면 Fallback이 날 위험이 있어 분리된 경로를 둔다.
   */
  const handleSelectByIntent = useCallback(
    (intent: string, displayLabel: string) => {
      addMessage({
        role: 'user',
        type: 'text',
        content: displayLabel,
      });

      // API 호출 intent는 Q&A 데이터 없이 별도 흐름으로 처리.
      if (intent === 'balance_check') {
        void handleBalanceCheck();
        return;
      }

      setTyping(true);

      setTimeout(() => {
        setTyping(false);

        const qa = findByIntent(intent, qaDatabase, authStatus);

        if (!qa) {
          // intent에 해당하는 Q&A가 없음 — 데이터 미비. 운영 알림용 로그 출력.
          console.error(
            `[Chatbot] Intent "${intent}" 에 해당하는 Q&A가 없습니다`
          );
          addMessage({
            role: 'bot',
            type: 'text',
            content:
              '죄송합니다. 해당 정보를 준비 중입니다.\n\n급하시면 1:1 온라인 상담으로 문의해 주세요.',
          });
          return;
        }

        if (qa.auth_required === 'required' && authStatus !== 'authenticated') {
          addMessage({
            role: 'bot',
            type: 'login_prompt',
            content:
              qa.unauth_answer ??
              `${qa.question_main}은(는) 로그인 후 이용 가능합니다.`,
            loginUrl: '/login',
          });
          return;
        }

        const answerText =
          qa.auth_required === 'mixed' &&
          authStatus !== 'authenticated' &&
          qa.unauth_answer
            ? qa.unauth_answer
            : qa.answer;

        addMessage({
          role: 'bot',
          type: 'text',
          content: answerText,
          ...(qa.related_links.length > 0 ? { links: qa.related_links } : {}),
          ...(qa.cta_buttons && qa.cta_buttons.length > 0
            ? { ctaButtons: qa.cta_buttons }
            : {}),
        });
      }, typingDelay);
    },
    [addMessage, setTyping, qaDatabase, authStatus, typingDelay, handleBalanceCheck]
  );

  return { handleSend, handleSelectByIntent };
}
